/**
 * Tavus Avatar Provider
 *
 * Implementation of IAvatarProvider for Tavus CVI (Conversational Video Interface).
 * Uses the Daily.co SDK for WebRTC video calls with Tavus replicas.
 */

import Daily from '@daily-co/daily-js'
import type { DailyCall } from '@daily-co/daily-js'

import type {
  IAvatarProvider,
  AvatarInfo,
  AvatarSessionOptions,
  HealthStatus,
  AvatarSession,
  AvatarSessionState,
  AvatarSessionEvents,
  ExpressionType,
} from './IAvatarProvider'

// Tavus API types
interface TavusReplica {
  replica_id: string
  replica_name: string
  status: string
  thumbnail_video_url?: string
  training_progress?: string
}

interface TavusConversationResponse {
  conversation_id: string
  conversation_url: string
  status: string
}

const TAVUS_API_BASE = 'https://tavusapi.com/v2'

export class TavusProvider implements IAvatarProvider {
  readonly id = 'tavus'
  readonly name = 'Tavus'
  readonly type = 'tavus' as const

  private apiKey: string | null = null
  private replicas: AvatarInfo[] = []

  async initialize(apiKey: string): Promise<void> {
    this.apiKey = apiKey
    // Verify the API key by fetching replicas
    await this.getAvatars()
  }

  isInitialized(): boolean {
    return this.apiKey !== null
  }

  async getAvatars(): Promise<AvatarInfo[]> {
    if (!this.apiKey) {
      throw new Error('Tavus provider not initialized')
    }

    try {
      const response = await fetch(`${TAVUS_API_BASE}/replicas`, {
        headers: {
          'x-api-key': this.apiKey,
        },
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch replicas: ${response.statusText}`)
      }

      const data = await response.json()
      this.replicas = (data.data || []).map((replica: TavusReplica) => ({
        id: replica.replica_id,
        name: replica.replica_name,
        description: `Status: ${replica.status}`,
        previewUrl: replica.thumbnail_video_url,
      }))

      return this.replicas
    } catch (error) {
      console.error('Failed to fetch Tavus replicas:', error)
      throw error
    }
  }

  async createSession(
    options: AvatarSessionOptions,
    events: AvatarSessionEvents
  ): Promise<AvatarSession> {
    if (!this.apiKey) {
      throw new Error('Tavus provider not initialized')
    }

    return new TavusSession(this.apiKey, options, events)
  }

  async healthCheck(): Promise<HealthStatus> {
    if (!this.apiKey) {
      return {
        isHealthy: false,
        message: 'Provider not initialized',
      }
    }

    try {
      const start = Date.now()
      const response = await fetch(`${TAVUS_API_BASE}/replicas`, {
        headers: {
          'x-api-key': this.apiKey,
        },
      })
      const latency = Date.now() - start

      return {
        isHealthy: response.ok,
        latency,
        message: response.ok ? 'Connected' : response.statusText,
      }
    } catch (error) {
      return {
        isHealthy: false,
        message: error instanceof Error ? error.message : 'Unknown error',
      }
    }
  }

  async destroy(): Promise<void> {
    this.apiKey = null
    this.replicas = []
  }
}

// ---------------------------------------------------------------------------
// Echo Persona Management
// ---------------------------------------------------------------------------

const ECHO_PERSONA_STORAGE_KEY = 'tavus_echo_persona_id'

/**
 * Get or create a shared Tavus echo-mode persona.
 * Echo mode disables ASR, LLM, TTS, and turn-taking so the replica only
 * speaks text sent via `conversation.echo`.
 */
async function getOrCreateEchoPersona(apiKey: string): Promise<string> {
  // Check cache
  const cached = localStorage.getItem(ECHO_PERSONA_STORAGE_KEY)
  if (cached) {
    // Validate it still exists
    try {
      const res = await fetch(`${TAVUS_API_BASE}/personas/${cached}`, {
        headers: { 'x-api-key': apiKey },
      })
      if (res.ok) return cached
    } catch {
      // Persona was deleted or network error — recreate
    }
    localStorage.removeItem(ECHO_PERSONA_STORAGE_KEY)
  }

  // Create a new echo persona
  const response = await fetch(`${TAVUS_API_BASE}/personas`, {
    method: 'POST',
    headers: {
      'x-api-key': apiKey,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      persona_name: 'Expert RAG Echo Renderer',
      pipeline_mode: 'echo',
      system_prompt: '',
      context: '',
    }),
  })

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(`Failed to create echo persona: ${response.statusText} - ${errorText}`)
  }

  const data = await response.json()
  const personaId: string = data.persona_id
  localStorage.setItem(ECHO_PERSONA_STORAGE_KEY, personaId)
  return personaId
}

/**
 * Tavus Avatar Session
 *
 * Manages a Tavus CVI conversation using the Daily.co SDK for WebRTC.
 */
class TavusSession implements AvatarSession {
  private _state: AvatarSessionState = 'idle'
  private conversationId: string | null = null
  private echoPersonaId: string | null = null
  private call: DailyCall | null = null
  private _videoElement: HTMLVideoElement | null = null
  private remoteAudioElement: HTMLAudioElement | null = null

  // Batched echo queue: sentences are queued while the avatar is speaking,
  // then joined and sent as one echo when the avatar stops.
  private echoQueue: string[] = []
  private isEchoSpeaking = false

  constructor(
    private apiKey: string,
    private options: AvatarSessionOptions,
    private events: AvatarSessionEvents
  ) {}

  get state(): AvatarSessionState {
    return this._state
  }

  get videoElement(): HTMLVideoElement | null {
    return this._videoElement
  }

  private setState(newState: AvatarSessionState) {
    this._state = newState
    this.events.onStateChange?.(newState)
  }

  private get isEchoMode(): boolean {
    return this.options.pipelineMode === 'expert_rag'
  }

  async start(): Promise<void> {
    if (this.call) {
      throw new Error('Session already started')
    }

    this.setState('connecting')

    try {
      // Step 1: For echo mode, ensure we have an echo persona
      if (this.isEchoMode) {
        const echoPersonaId = await getOrCreateEchoPersona(this.apiKey)
        this.echoPersonaId = echoPersonaId
      }

      // Step 2: Create a Tavus conversation and get the Daily room URL
      const conversation = await this.createConversation()
      this.conversationId = conversation.conversation_id

      // Step 3: Create a Daily call object (no local video, audio only)
      this.call = Daily.createCallObject({
        videoSource: false,
      })

      // Step 4: Wire up Daily event handlers
      this.setupDailyEvents()

      // Step 5: Join the Daily room
      await this.call.join({ url: conversation.conversation_url })

      // Step 6: In full mode, unmute mic so replica can hear us.
      // In echo mode, Tavus ignores audio (ASR disabled), so skip this.
      if (!this.isEchoMode) {
        await this.call.setLocalAudio(true)
      }

      this.setState('connected')
    } catch (error) {
      console.error('Failed to start Tavus session:', error)
      this.setState('error')
      this.events.onError?.(error instanceof Error ? error : new Error('Failed to start session'))
      throw error
    }
  }

  async stop(): Promise<void> {
    // Leave and destroy the Daily call
    if (this.call) {
      try {
        await this.call.leave()
      } catch {
        // Already left / destroyed — safe to ignore
      }
      this.call.destroy()
      this.call = null
    }

    // End conversation via Tavus API
    if (this.conversationId) {
      try {
        await this.endConversation()
      } catch (error) {
        console.warn('Failed to end conversation:', error)
      }
      this.conversationId = null
    }

    this._videoElement = null

    if (this.remoteAudioElement) {
      this.remoteAudioElement.srcObject = null
      this.remoteAudioElement = null
    }

    // Clear echo queue
    this.echoQueue = []
    this.isEchoSpeaking = false

    this.setState('idle')
  }

  sendAudio(_audioData: Float32Array | Int16Array): void {
    // Audio is captured and sent by the Daily SDK automatically
  }

  sendText(text: string): void {
    if (!this.call || !this.conversationId) {
      console.warn('Cannot send text: not connected')
      return
    }

    this.call.sendAppMessage(
      {
        message_type: 'conversation',
        event_type: 'conversation.respond',
        conversation_id: this.conversationId,
        properties: { text },
      },
      '*'
    )
    this.setState('thinking')
  }

  echo(text: string): void {
    if (!this.call || !this.conversationId) {
      console.warn('Cannot echo: not connected')
      return
    }

    if (!this.isEchoSpeaking) {
      // Avatar isn't speaking — send immediately
      this.sendEcho(text)
      this.isEchoSpeaking = true
    } else {
      // Avatar is currently speaking — queue for next batch
      this.echoQueue.push(text)
    }
  }

  private sendEcho(text: string): void {
    if (!this.call || !this.conversationId) return

    this.call.sendAppMessage(
      {
        message_type: 'conversation',
        event_type: 'conversation.echo',
        conversation_id: this.conversationId,
        properties: { text },
      },
      '*'
    )
    this.setState('speaking')
  }

  setExpression(_expression: ExpressionType): void {
    // Tavus CVI does not support explicit expression control
  }

  interrupt(): void {
    if (!this.call || !this.conversationId) {
      return
    }

    // Clear echo queue so pending sentences are discarded
    this.echoQueue = []
    this.isEchoSpeaking = false

    this.call.sendAppMessage(
      {
        message_type: 'conversation',
        event_type: 'conversation.interrupt',
        conversation_id: this.conversationId,
        properties: {},
      },
      '*'
    )
    this.setState('ready')
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  private async createConversation(): Promise<TavusConversationResponse> {
    const body: Record<string, unknown> = {
      replica_id: this.options.replicaId,
      conversation_name: `session-${Date.now()}`,
      properties: {
        enable_recording: false,
        apply_greenscreen: false,
      },
    }

    if (this.isEchoMode && this.echoPersonaId) {
      // Echo mode: use the echo persona (pipeline_mode: "echo")
      body.persona_id = this.echoPersonaId
    } else if (this.options.personaId) {
      // Full mode: use the user-specified persona (if any)
      body.persona_id = this.options.personaId
    }

    if (this.options.initialMessage) {
      body.custom_greeting = this.options.initialMessage
    }

    const response = await fetch(`${TAVUS_API_BASE}/conversations`, {
      method: 'POST',
      headers: {
        'x-api-key': this.apiKey,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to create conversation: ${response.statusText} - ${errorText}`)
    }

    return response.json()
  }

  private async endConversation(): Promise<void> {
    if (!this.conversationId) return

    await fetch(`${TAVUS_API_BASE}/conversations/${this.conversationId}/end`, {
      method: 'POST',
      headers: {
        'x-api-key': this.apiKey,
      },
    })
  }

  private setupDailyEvents(): void {
    if (!this.call) return

    // Remote participant tracks (video / audio from the replica)
    this.call.on('track-started', (event) => {
      if (!event || !event.participant || event.participant.local) return

      const track = event.track
      if (track.kind === 'video') {
        const video = document.createElement('video')
        video.srcObject = new MediaStream([track])
        video.autoplay = true
        video.playsInline = true
        this._videoElement = video
        this.events.onVideoReady?.(video)
        this.setState('ready')
      }

      if (track.kind === 'audio') {
        // Daily's createCallObject() does not auto-play remote audio —
        // we must attach it to an <audio> element ourselves.
        if (!this.remoteAudioElement) {
          this.remoteAudioElement = document.createElement('audio')
          this.remoteAudioElement.autoplay = true
        }
        this.remoteAudioElement.srcObject = new MediaStream([track])
        this.events.onAudioReady?.(new MediaStream([track]))
      }
    })

    // Tavus interaction-protocol messages
    this.call.on('app-message', (event) => {
      if (event?.data) {
        this.handleAppMessage(event.data)
      }
    })

    // Replica (remote participant) joins
    this.call.on('participant-joined', (event) => {
      if (event?.participant && !event.participant.local) {
        this.setState('connected')
      }
    })

    // Replica leaves
    this.call.on('participant-left', (event) => {
      if (event?.participant && !event.participant.local) {
        this.setState('disconnected')
      }
    })

    // Fatal call errors
    this.call.on('error', (event) => {
      const msg = event?.errorMsg
      this.events.onError?.(new Error(msg || 'Daily call error'))
      this.setState('error')
    })
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private handleAppMessage(data: any): void {
    const eventType: string | undefined = data?.event_type
    const props = data?.properties ?? {}

    switch (eventType) {
      case 'conversation.utterance':
        if (props.role === 'replica') {
          this.events.onMessage?.(props.text ?? '')
          this.setState('speaking')
        }
        this.events.onTranscript?.(props.text ?? '', props.is_final ?? false)
        break

      case 'conversation.replica.started_speaking':
        this.setState('speaking')
        break

      case 'conversation.replica.stopped_speaking':
        if (this.echoQueue.length > 0) {
          // Join all queued sentences and send as one batch
          const batch = this.echoQueue.join(' ')
          this.echoQueue = []
          this.sendEcho(batch)
        } else {
          this.isEchoSpeaking = false
          this.setState('ready')
        }
        break

      case 'conversation.error':
        this.events.onError?.(new Error(props.message ?? 'Unknown Tavus error'))
        this.setState('error')
        break
    }
  }
}

// Export singleton instance
export const tavusProvider = new TavusProvider()
